import React, { useCallback, useMemo, useState } from "react";
import { useSelector } from "react-redux";
import styled from "styled-components";

import { BasePreviewProps, DeviceState } from "../../../types/media-preview";
import {
  getYouTubeEmbedUrl,
  getYouTubeThumbnailUrl,
  getYouTubeVideoId,
} from "../../../utils/youtube";

export interface YouTubePreviewProps extends BasePreviewProps {
  /** PC でホバー中だけ自動再生プレビューを差し込む（旧 Django 互換）。 */
  hoverPlay?: boolean;
  /** モーダル等で iframe を初期再生したい場合に true。 */
  autoPlay?: boolean;
  /** ネイティブ操作 UI を表示するか（モーダル用途）。 */
  showControls?: boolean;
}

/**
 * YouTube 埋め込みコンテナ。
 *
 * `fixed-height` ではサムネイル画像（16:9）を高さ固定・幅 auto で表示し、
 * `fill` / `auto` ではアスペクト比 16:9 をベースに親要素いっぱいに広げる。
 * いずれもホバー時のみ iframe を載せ替えることで、停止時はネットワーク負荷を抑える。
 */
const Container = styled.div<{
  $mode: "fixed-width" | "fixed-height" | "auto" | "fill";
  $width?: string;
  $height?: string;
  $maxWidth?: string;
  $maxHeight?: string;
}>`
  position: relative;
  overflow: hidden;
  border-radius: 6px;
  background: #000;

  ${(props) => {
    switch (props.$mode) {
      case "fixed-width":
        return `
          width: ${props.$width || "100%"};
          aspect-ratio: 16 / 9;
          max-height: ${props.$maxHeight || "100dvh"};
        `;
      case "fixed-height":
        return `
          height: ${props.$height || "auto"};
          aspect-ratio: 16 / 9;
          width: auto;
          ${props.$maxWidth ? `max-width: ${props.$maxWidth};` : ""}
        `;
      case "fill":
        return `
          width: 100%;
          aspect-ratio: 16 / 9;
          max-height: 100%;
        `;
      case "auto":
      default:
        return `
          width: 100%;
          aspect-ratio: 16 / 9;
          ${props.$maxWidth ? `max-width: ${props.$maxWidth};` : ""}
          max-height: ${props.$maxHeight || "100dvh"};
        `;
    }
  }}
`;

const Thumb = styled.img`
  width: 100%;
  height: 100%;
  object-fit: cover;
  display: block;
`;

const Frame = styled.iframe`
  position: absolute;
  inset: 0;
  width: 100%;
  height: 100%;
  border: 0;
  display: block;
`;

const PlayBadge = styled.div`
  position: absolute;
  inset: 0;
  display: flex;
  align-items: center;
  justify-content: center;
  pointer-events: none;

  &::after {
    content: "play_arrow";
    font-family: "Material Symbols Rounded";
    font-size: 48px;
    color: rgba(255, 255, 255, 0.95);
    background: rgba(0, 0, 0, 0.45);
    width: 64px;
    height: 64px;
    border-radius: 50%;
    display: flex;
    align-items: center;
    justify-content: center;
    text-shadow: 0 2px 8px rgba(0, 0, 0, 0.5);
  }
`;

const Fallback = styled.div`
  width: 100%;
  height: 100%;
  display: flex;
  align-items: center;
  justify-content: center;
  background: var(--soremo-background, #f5f5f5);
  color: var(--soremo-light-gray, #888);
  font-size: 12px;
  padding: 8px;
  text-align: center;
`;

const YouTubePreview: React.FC<YouTubePreviewProps> = ({
  src,
  thumbnailSrc = null,
  sizeMode = { mode: "auto" },
  hoverPlay = false,
  autoPlay = false,
  showControls = false,
  className,
  style,
}) => {
  const { isMobile } = useSelector((state: { device: DeviceState }) => state.device);

  const videoId = useMemo(() => getYouTubeVideoId(src), [src]);
  const [isHovering, setIsHovering] = useState(false);

  const showIframe = autoPlay || (hoverPlay && isHovering && !isMobile);

  const handleMouseEnter = useCallback(() => {
    if (hoverPlay && !isMobile) setIsHovering(true);
  }, [hoverPlay, isMobile]);

  const handleMouseLeave = useCallback(() => {
    if (hoverPlay && !isMobile) setIsHovering(false);
  }, [hoverPlay, isMobile]);

  if (!videoId) {
    return (
      <Container
        $mode={sizeMode.mode}
        $width={sizeMode.width}
        $height={sizeMode.height}
        $maxWidth={sizeMode.maxWidth}
        $maxHeight={sizeMode.maxHeight}
        className={className}
        style={style}
      >
        <Fallback>YouTube リンクを表示できません</Fallback>
      </Container>
    );
  }

  const thumbUrl = thumbnailSrc || getYouTubeThumbnailUrl(videoId);
  const embedUrl = getYouTubeEmbedUrl(videoId, {
    autoplay: true,
    mute: !showControls,
    controls: showControls,
    rel: false,
    playsInline: true,
  });

  return (
    <Container
      $mode={sizeMode.mode}
      $width={sizeMode.width}
      $height={sizeMode.height}
      $maxWidth={sizeMode.maxWidth}
      $maxHeight={sizeMode.maxHeight}
      className={className}
      style={style}
      onMouseEnter={handleMouseEnter}
      onMouseLeave={handleMouseLeave}
    >
      {showIframe ? (
        <Frame
          src={embedUrl}
          title="YouTube video player"
          allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture; web-share"
          allowFullScreen
        />
      ) : (
        <>
          <Thumb src={thumbUrl} alt="" loading="lazy" />
          <PlayBadge />
        </>
      )}
    </Container>
  );
};

export default YouTubePreview;
